import { useAnimate } from "framer-motion"
import { graphql, Link, useStaticQuery } from "gatsby"
import parse from "html-react-parser"
import React, { useEffect, useRef, useState } from "react"
import "./Header.css"

function useScrollDirection() {
  const [scrollDirection, setScrollDirection] = React.useState(null)

  React.useEffect(() => {
    let lastScrollY = window.pageYOffset

    const updateScrollDirection = () => {
      const scrollY = window.pageYOffset
      const direction = scrollY > lastScrollY ? "down" : "up"
      if (
        direction !== scrollDirection &&
        (scrollY - lastScrollY > 1 || scrollY - lastScrollY < -1)
      ) {
        setScrollDirection(direction)
      }
      lastScrollY = scrollY > 0 ? scrollY : 0
    }
    window.addEventListener("scroll", updateScrollDirection) // add event listener

    return () => {
      window.removeEventListener("scroll", updateScrollDirection) // clean up
    }
  }, [scrollDirection])

  return scrollDirection
}

const Header = () => {
  const headerData = useStaticQuery(graphql`
    query {
      wp {
        themeGeneralSettings {
          fluxDna {
            mainLogo {
              altText
              mediaItemUrl
              title
            }
            enquiry {
              target
              title
              url
            }
            mainMenu {
              menuLink {
                target
                title
                url
              }
            }
            sidebarMenu1 {
              innerMenu
              mainMenu {
                target
                title
                url
              }
              innerMenuLink {
                subMenuImage {
                  altText
                  mediaItemUrl
                  title
                }
                subMenuLink {
                  target
                  title
                  url
                }
              }
            }
            sidebarMenu2 {
              menu {
                target
                title
                url
              }
            }
            bookButton {
              target
              title
              url
            }
            bookDescription
            bookHeading
            enquiryButton {
              target
              title
              url
            }
            enquiryDescription
            enquiryHeading
            contactNumber
            emailAddress
            headerSocialMedia {
              link {
                target
                title
                url
              }
              icon {
                altText
                mediaItemUrl
                title
              }
            }
          }
        }
      }
    }
  `)
  const headerDataList = headerData.wp.themeGeneralSettings.fluxDna

  const [menuOpen, setMenuOpen] = useState(false)
  const [booktableOpen, setBooktableOpen] = useState(false)

  const [scope, animate] = useAnimate()
  const [scope2, animate2] = useAnimate()

  useEffect(() => {
    const menuAnimations = menuOpen
      ? [
          [".header", { background: "transparent" }, { duration: 0.01 }],
          [
            ".logo",
            {
              opacity: 1,
              filter: "brightness(0) saturate(100%) invert(30%) sepia(9%) saturate(453%) hue-rotate(197deg) brightness(97%) contrast(89%)",
            },
            { duration: 0.6 },
          ], // Ensure logo is fully visible and apply filter
          [".header-menu", { opacity: 0 }, { duration: 0.6 }],
          [
            ".main-nav",
            { clipPath: "polygon(100% 0px, 0px 0px, 0px 100%, 100% 100%)" },
            { duration: 0.6 },
          ],
          [
            ".navMenu",
            { transform: "translateX(0px)", opacity: 1 },
            { duration: 0.5, at: "4" },
          ],
        ]
      : [
          [
            ".navMenu",
            { transform: "translateX(50px)", opacity: 0 },
            { duration: 0.5, at: "1" },
          ],
          [
            ".main-nav",
            { clipPath: "polygon(0 0, 0 0, 0 100%, 0% 100%)" },
            { duration: 0.6 },
          ],
          [".logo,.header-menu", { opacity: 1, filter: "none" }, { duration: 0.6 }], // Reset filter when menu is closed
          [".header", { background: "" }, { duration: 0.01 }],
        ]
    animate([...menuAnimations])
  }, [scope, menuOpen, animate])
  
  useEffect(() => {
    if (menuOpen) {
      document.body.classList.add("menu-open")
    } else {
      document.body.classList.remove("menu-open")
    }
  }, [menuOpen])

  // Book Table
  useEffect(() => {
    const bookAnimations = booktableOpen
      ? [
        [".make-booking", { transform: "translateX(0%)" }, { duration: 0.6 }],
        [
          ".bookMenu",
          { transform: "translateX(0px)", opacity: 1 },
          { duration: 0.5, at: "4" },
        ],
      ]
      : [
        [
          ".bookMenu",
          { transform: "translateX(50px)", opacity: 0 },
          { duration: 0.5, at: "1" },
        ],
        [
          ".make-booking",
          { transform: "translateX(110%)" },
          { duration: 0.6 },
        ],
      ]
    animate([...bookAnimations])
  }, [scope2, booktableOpen, animate])

  useEffect(() => {
    if (booktableOpen) {
      document.body.classList.add("book-open")
    } else {
      document.body.classList.remove("book-open")
    }
  }, [booktableOpen])

  const MenuToggle = ({ toggle }) => (
    <div
      id="navbar-icon"
      className="navbar-icon cursor-pointer grid gap-y-5"
      onClick={toggle}
      onKeyDown={toggle}
      aria-hidden="true"
    >
      <img
        src="../../images/hamburger-icon.svg"
        className="menu-icon"
        width={27}
        height={19}
        alt="hamburger"
      />
      <Link to="#" className="grid gap-y-1">
        <img
          src="../../images/close-hamburger-icon.svg"
          width={22}
          height={22}
          alt="close-icon"
          className="close-icon"
          loading="eager"
        />
      </Link>
    </div>
  )

  const [hoveredItemId, setHoveredItemId] = useState("0-0")
  const handleMouseEnter = index => {
    setHoveredItemId(index)
  }

  const handleMouseLeave = () => {
    setHoveredItemId("0-0")
  }

  // Header Sticky
  const scrollDirection = useScrollDirection()

 useEffect(() => {
  const handleScroll = () => {
    const header = document.querySelector("header");
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    if (scrollTop === 0) {
      header.classList.remove("header-sticky");
      document.querySelector(".logo").style.filter = "none"; // Reset filter when at the top
    } else if (scrollDirection === "up") {
      header.classList.add("header-sticky");
      document.querySelector(".logo").style.filter = "brightness(0) saturate(100%) invert(30%) sepia(9%) saturate(453%) hue-rotate(197deg) brightness(97%) contrast(89%)"; // Apply filter when scrolling up
    } else {
      header.classList.remove("header-sticky");
      document.querySelector(".logo").style.filter = "none"; // Reset filter when scrolling down
    }
  };

  window.addEventListener("scroll", handleScroll);

  return () => {
    window.removeEventListener("scroll", handleScroll);
  };
}, [scrollDirection]);
  const [resBookBtn, setResBookBtn] = useState(false)

  const BookToggle = ({ booktoggle }) => (
    <div
      className=" btn-custom book-btn"
      onClick={booktoggle}
      onKeyDown={booktoggle}
      aria-hidden="true"
    >
      {headerDataList.enquiry && (
        <Link
          to={"#"}
          target={headerDataList.enquiry.target}
          id="book-btn"
          className={`btn ${resBookBtn ? "btn-grey mdscreen:btn-white mdscreen:bg-grey-100 mdscreen:hover:bg-white-100" : "btn-white"}`}
        >
          {headerDataList.enquiry.title}
        </Link>
      )}
    </div>
  )

  const handleSubmenu = e => {
    const parentHoverable = e.target.closest(".hoverable")
    if (parentHoverable) {
      parentHoverable.classList.toggle("mobile-submenu")
    }
  }

  return (
    <React.Fragment>
      <section ref={scope}>
        <header
          className={`header py-35 fixed z-9999 w-full headersingle transition-common ${scrollDirection === "down"
            ? "-top-[160px] -z-99 transition-all duration-[750ms] ease-in-out"
            : "top-0 transition-all duration-[750ms] ease-in-out"
            } ${scrollDirection === "up" && window.pageYOffset !== "0"
              ? "header-sticky"
              : ""
            }`}
        >
          <div className="container-fluid-sm relative">
            <div className="flex flex-wrap justify-between items-center">
              <div className="header-left flex flex-wrap items-center gap-x-50 mdscreen:order-2 xlscreen:gap-x-[30px] mdscreen:justify-end">
                <MenuToggle
                  toggle={() => {
                    if (window.matchMedia("(max-width: 991px)").matches) {
                      setResBookBtn(!resBookBtn)
                    }
                    setMenuOpen(!menuOpen)
                  }}
                  onClick={() => {
                    !menuOpen ? setMenuOpen(true) : setMenuOpen(false)
                  }}
                ></MenuToggle>
                {headerDataList?.mainMenu && (
                  <ul className="header-menu flex flex-wrap gap-x-50 mdscreen2:hidden xlscreen:gap-x-[30px]">
                    {headerDataList.mainMenu.map((itemMenu, i) => (
                      <li key={i}>
                        <Link
                          className="text-13 xlscreen:text-12 text-gold-200 tracking-[0.52px] uppercase font-400 hover:text-green"
                          to={itemMenu.menuLink.url}
                          target={itemMenu.menuLink.target}
                        >
                          {parse(itemMenu.menuLink.title)}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="logo mdscreen:order-1">
                <Link to="/">
                  <img
                    src={headerDataList.mainLogo.mediaItemUrl}
                    width={195}
                    height={60}
                    loading="eager"
                    alt="Logo"
                    className="mdscreen:w-[120px] mdscreen:h-auto"
                  />
                </Link>
              </div>
              <div className="header-right flex flex-wrap items-center mdscreen:hidden">
                <BookToggle
                  booktoggle={() => {
                    setBooktableOpen(!booktableOpen)
                  }}
                  onClick={() => {
                    !booktableOpen
                      ? setBooktableOpen(true)
                      : setBooktableOpen(false)
                  }}
                ></BookToggle>
              </div>
            </div>
          </div>
        </header>

        <div className="main-nav">
          <div className="flex flex-wrap h-full w-full">
            <div className="sidenav-left w-7/12 h-full mdscreen2:w-full">
              <div className="px-85 lg:!pr-50 lgscreen:px-45 pt-[140px] smscreen2:pt-[100px] smscreen2:pb-50 h-full xlscreen:pl-80 xlscreen:pr-50 mdscreen:!px-45 smscreen:!px-20 smscreen2:pr-0 relative">
                <div className="mainNav__col relative navMenu">
                  <div className="menuOption_wrapper relative">
                    <ul className="navMenu hover-underline-solid text-white grid gap-y-25 top-navbar z-99 w-5/12 mdscreen:w-full">
                      {headerDataList?.sidebarMenu1.length > 0 &&
                        headerDataList.sidebarMenu1.map((itemMenu, im) => {
                          return (
                            <li
                              className={`${itemMenu?.innerMenu === true ? "hoverable" : ""
                                } text-24 laptop:text-22 xlscreen2:text-20 leading-32 font-400 opacity-70 hover:font-500 hover:opacity-100 uppercase`}
                              key={im}
                            >
                              <div className="flex flex-wrap items-center gap-x-15">
                                <Link
                                  to={itemMenu.mainMenu.url}
                                  target={itemMenu.mainMenu.target}
                                >
                                  {itemMenu.mainMenu.title}
                                </Link>
                                {itemMenu?.innerMenu === true && (
                                  <span
                                    className="cursor-pointer menuarrow transition-all duration-300"
                                    onClick={e => {
                                      handleSubmenu(e)
                                    }}
                                    onKeyDown={e => {
                                      handleSubmenu(e)
                                    }}
                                    aria-hidden="true"
                                  >
                                    <img
                                      src="../../images/menu-arrow.svg"
                                      alt=""
                                    />
                                  </span>
                                )}
                              </div>
                              {itemMenu?.innerMenu === true && (
                                <div className="mega-menu">
                                  <ul className="lg:space-y-15 lgscreen:space-y-10">
                                    {itemMenu?.innerMenuLink.length > 0 &&
                                      itemMenu.innerMenuLink.map(
                                        (itemS, index) => {
                                          const keyi = im + "-" + index
                                          return (
                                            <li key={keyi}>
                                              <Link
                                                to={itemS.subMenuLink.url}
                                                target={itemS.subMenuLink.target}
                                                onMouseEnter={() =>
                                                  handleMouseEnter(keyi)
                                                }
                                                onMouseLeave={handleMouseLeave}
                                              >
                                                {itemS.subMenuLink.title}
                                              </Link>
                                            </li>
                                          )
                                        }
                                      )}
                                  </ul>
                                </div>
                              )}
                            </li>
                          )
                        })}
                    </ul>
                    <div className="right-hover-items w-7/12 mdscreen:w-full"></div>
                  </div>
                  <div className="navMenu sub-navbar pt-45 relative">
                    {headerDataList?.sidebarMenu2 && (
                      <ul className="grid gap-y-20 grid-cols-2 lgscreen:grid-cols-1">
                        {headerDataList.sidebarMenu2.map((itemMenu, k) => (
                          <li key={k}>
                            <Link
                              to={itemMenu.menu.url}
                              target={itemMenu.menu.target}
                            >
                              {parse(itemMenu.menu.title)}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                    <div className="pt-25 md:hidden">
                      <BookToggle
                        booktoggle={() => {
                          setBooktableOpen(!booktableOpen)
                        }}
                        onClick={() => {
                          !booktableOpen
                            ? setBooktableOpen(true)
                            : setBooktableOpen(false)
                        }}
                      ></BookToggle>
                    </div>
                  </div>
                  <div className="sideNav-social pt-30 navMenu">
                    <div className="grid grid-cols-2 mdscreen:grid-cols-1 gap-y-20">
                      {headerDataList?.headerSocialMedia && (
                        <ul className="social-media flex flex-wrap items-center gap-10">
                          {headerDataList.headerSocialMedia.map((item, s) => (
                            <li key={s}>
                              <Link to={item.link.url} className="group/social-item" target={item.link.target}>
                                <img src={item.icon.mediaItemUrl} alt={item.icon.title} height={20} width={20} className="h-[20px] w-[20px] group-hover/social-item:-translate-y-[5px] transition-common" />
                              </Link>
                            </li>
                          ))}
                        </ul>
                      )}
                      <ul className="contact-info flex flex-wrap items-center gap-y-[10px]">
                        {headerDataList?.contactNumber && (
                          <li className="pr-30 last:pr-0">
                            <Link to={`tel:${headerDataList.contactNumber}`} className="text-13 text-grey-200 hover:opacity-70">
                              {headerDataList.contactNumber}
                            </Link>
                          </li>
                        )}
                        {headerDataList?.emailAddress && (
                          <li className="pr-30 last:pr-0">
                            <Link to={`mailto:${headerDataList.emailAddress}`} className="text-13 text-grey-200 underline hover:opacity-70">
                              {headerDataList.emailAddress}
                            </Link>
                          </li>
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="sidenav-right w-5/12 h-full relative mdscreen2:hidden">
              {headerDataList?.sidebarMenu1.length > 0 &&
                headerDataList.sidebarMenu1.map((itemimg, ig) => (
                  <div className="image_wrapper" key={ig}>
                    {Array.isArray(itemimg.innerMenuLink) &&
                      itemimg.innerMenuLink.length > 0 &&
                      itemimg.innerMenuLink.map((itemi, index) => {
                        const keyi = ig + "-" + index
                        return (
                          <div
                            key={keyi}
                            className={
                              keyi === hoveredItemId
                                ? "show-image"
                                : "hide-image"
                            }
                          >
                            {itemi.subMenuImage && (
                              <img
                                src={itemi.subMenuImage.mediaItemUrl}
                                alt={itemi.subMenuImage.altText}
                                height={539}
                                width={748}
                                loading="lazy"
                              />
                            )}
                          </div>
                        )
                      })}
                  </div>
                ))}

              <div className="arrow-pattern"></div>
              <div className="ring-group">
                          <img
                            src="/images/vertical-ring-group.svg"
                          />
                        </div>
            </div>
          </div>
        </div>

        <div className="make-booking pt-[170px] mdscreen:pt-100 bg-grey-100">
          <div
            className="book-close"
            onClick={() => {
              !booktableOpen ? setBooktableOpen(true) : setBooktableOpen(false)
            }}
            onKeyDown={() => {
              !booktableOpen ? setBooktableOpen(true) : setBooktableOpen(false)
            }}
            aria-hidden="true"
          >
            <img
              src="../../images/closemark-white.svg"
              alt="close-mark"
              height={22}
              width={22}
              loading="lazy"
            />
          </div>
          <div className="pr-50 pl-90 mdscreen:pb-25 laptop:px-35 relative mdscreen:overflow-auto h-full overflow-y-auto">
            <div className="flex flex-wrap justify-end gap-y-[60px] pb-40">
              <div className="relative book-menu bookMenu content-block text-white-100 text-right pb-60">
                {headerDataList.bookHeading && (
                  <h5 className="text-21 leading-28 font-primary font-400 uppercase text-white-200">
                    {headerDataList.bookHeading}
                  </h5>
                )}
                {headerDataList.bookDescription && (
                  <div className="content pt-10">
                    <p>{headerDataList.bookDescription}</p>
                  </div>
                )}
                {headerDataList.bookButton && (
                  <div className="pt-45">
                    <Link
                      to={headerDataList.bookButton.url}
                      className="btn btn-white"
                      target={headerDataList.bookButton.target}
                    >
                      {headerDataList.bookButton.title}
                    </Link>
                  </div>
                )}
              </div>
              <div className="bookMenu content-block text-white-100 text-right">
                {headerDataList.enquiryHeading && (
                  <h5 className="text-21 leading-28 font-primary font-400 uppercase text-white-200">
                    {headerDataList.enquiryHeading}
                  </h5>
                )}
                {headerDataList.enquiryDescription && (
                  <div className="content pt-10">
                    <p>{headerDataList.enquiryDescription}</p>
                  </div>
                )}
                {headerDataList.enquiryButton && (
                  <div className="pt-45">
                    <Link
                      to={headerDataList.enquiryButton.url}
                      target={headerDataList.enquiryButton.target}
                      className="btn btn-white"
                    >
                      {headerDataList.enquiryButton.title}
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </React.Fragment>
  )
}

export default Header
